import React, { useState, useEffect } from 'react';
// [Web UX 3. csomag (D), 2026-10-04] A kérés-hibák szövege a közös `apiErrorMessage`-en át megy (a szerver magyar
// indoklása mindhárom törzs-alakból / hálózati hibánál magyar mondat / különben „<tartalék> (HTTP n)"),
// a nyers `err.response?.data?.message` helyett. Őr: `utils/__tests__/rawErrorMessages.guard.test.ts`.
import { apiErrorMessage } from '../utils/apiErrors';
import {
  Box,
  Paper,
  Typography,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Alert,
  CircularProgress,
  Card,
  CardContent,
  Chip,
  Divider,
  Pagination,
  SelectChangeEvent,
} from '@mui/material';
import {
  Security as SecurityIcon,
  Person as PersonIcon,
  Event as EventIcon,
  AccessTime as AccessTimeIcon,
  Computer as ComputerIcon,
} from '@mui/icons-material';
import { DatePicker } from '@mui/x-date-pickers';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
// [Web UX 9 / T2, 2026-10-13] a dátumválasztók magyar feliratai (Cancel/OK/Select date helyett) — lásd utils/dateLocale.ts
import { HU_PICKER_LOCALE_TEXT } from '../utils/dateLocale';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs, { Dayjs } from 'dayjs';
import { useAuth } from '../contexts/AuthContext';
import { sosAccessService, SosAccessReportFilter, SosAccessReportItem, PagedResult } from '../services/sosAccessService';
import { userService } from '../services/userService';
import { formatServerDateTime } from '../utils/dateUtils';
import { UserListItem } from '../types/user';

const SosAccessLogReport: React.FC = () => {
  const { user: currentUser } = useAuth();
  const [users, setUsers] = useState<UserListItem[]>([]);
  const [selectedCoachId, setSelectedCoachId] = useState<string>('');
  const [selectedPlayerId, setSelectedPlayerId] = useState<string>('');
  const [startDate, setStartDate] = useState<Dayjs | null>(dayjs().subtract(30, 'day'));
  const [endDate, setEndDate] = useState<Dayjs | null>(dayjs());
  const [reportData, setReportData] = useState<PagedResult<SosAccessReportItem> | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string>('');
  const [pageNumber, setPageNumber] = useState(1);
  const pageSize = 20;

  useEffect(() => {
    loadUsers();
    // Load initial data
    loadReport();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadUsers = async () => {
    try {
      const result = await userService.getUsers();
      // Sort users alphabetically by name
      const sortedUsers = result.data
        .filter(user => user.isActive)
        .sort((a, b) => a.name.localeCompare(b.name, 'hu'));
      setUsers(sortedUsers);
    } catch (err) {
      console.error('Error loading users:', err);
    }
  };

  const loadReport = async (page: number = 1) => {
    try {
      setLoading(true);
      setError('');

      const filter: SosAccessReportFilter = {
        coachUserId: selectedCoachId || undefined,
        viewedUserId: selectedPlayerId || undefined,
        startDate: startDate?.format('YYYY-MM-DD'),
        endDate: endDate?.format('YYYY-MM-DD'),
        pageNumber: page,
        pageSize: pageSize,
      };

      const data = await sosAccessService.getAccessReport(filter);
      setReportData(data);
      setPageNumber(page);
    } catch (err: any) {
      console.error('SOS report loading error:', err);
      // [Web UX 3. csomag (D), 2026-10-04] a régi `|| err.message` tartalék az axios ANGOL mondatát írta ki
      // („Network Error", „Request failed with status code 500") — a közös segéd ezt magyarul adja.
      const errorMessage = apiErrorMessage(err, 'Hiba történt a kimutatás betöltésekor');
      setError(errorMessage);
      setReportData(null);
    } finally {
      setLoading(false);
    }
  };

  // [Web UX 1. csomag (4.3), 2026-10-03] A szerver az `AccessedAt`-ot UTC-ben menti (`SosAccessService.cs:344`), és a
  // JSON-ban NINCS „Z” (élő API-n mérve) — a régi `new Date(s)` helyi időnek vette, így a napló
  // 13:15-öt mutatott a valódi 15:15 helyett. A közös segéd UTC-nek veszi és budapesti időben írja ki.
  const formatDateTime = (dateTimeString: string): string => {
    if (!dateTimeString) return 'Nincs adat';
    return formatServerDateTime(dateTimeString, { fallback: 'Érvénytelen dátum' });
  };

  const getUsersByRole = (role: 'Player' | 'Coach') => {
    return users.filter(user => user.userRole === role);
  };

  const handlePageChange = (event: React.ChangeEvent<unknown>, value: number) => {
    loadReport(value);
  };

  const handleCoachChange = (event: SelectChangeEvent) => {
    setSelectedCoachId(event.target.value as string);
  };

  const handlePlayerChange = (event: SelectChangeEvent) => {
    setSelectedPlayerId(event.target.value as string);
  };

  const isAdmin = currentUser?.userRole === 'Administrator';

  if (!isAdmin) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error">
          Nincs jogosultságod az SOS napló megtekintéséhez.
        </Alert>
      </Box>
    );
  }

  return (
    // [Web UX 9 / T2, 2026-10-13] `adapterLocale="hu"` PÓTOLVA: nélküle az AdapterDayjs KÉNYSZERÍTETTEN angol
    // (`getCurrentLocaleCode: this.locale || 'en'`) — angol hónapnevek („October 2026"), vasárnapos hét, amerikai MM/DD/YYYY mező.
    <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale="hu" localeText={HU_PICKER_LOCALE_TEXT}>
      <Box sx={{ p: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
          <SecurityIcon sx={{ fontSize: 40, color: 'error.main' }} />
          <Box>
            <Typography variant="h4" gutterBottom>
              SOS Vészhelyzeti Adathozzáférési Napló
            </Typography>
            <Typography variant="body2" color="text.secondary">
              GDPR-kompatibilis audit napló a vészhelyzeti adathozzáférésekről
            </Typography>
          </Box>
        </Box>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
            {error}
          </Alert>
        )}

        {/* Filters */}
        <Paper sx={{ p: 3, mb: 3 }}>
          <Typography variant="h6" gutterBottom>
            Szűrési beállítások
          </Typography>
          <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'center' }}>
            <Box sx={{ flex: 1, minWidth: 200 }}>
              <FormControl fullWidth>
                <InputLabel>Edző</InputLabel>
                <Select
                  value={selectedCoachId}
                  label="Edző"
                  onChange={handleCoachChange}
                >
                  <MenuItem value="">Minden edző</MenuItem>
                  {getUsersByRole('Coach').map((user) => (
                    <MenuItem key={user.userId} value={user.userId}>
                      {user.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Box>

            <Box sx={{ flex: 1, minWidth: 200 }}>
              <FormControl fullWidth>
                <InputLabel>Játékos</InputLabel>
                <Select
                  value={selectedPlayerId}
                  label="Játékos"
                  onChange={handlePlayerChange}
                >
                  <MenuItem value="">Minden játékos</MenuItem>
                  {getUsersByRole('Player').map((user) => (
                    <MenuItem key={user.userId} value={user.userId}>
                      {user.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Box>

            <Box sx={{ flex: 1, minWidth: 200 }}>
              <DatePicker
                label="Kezdő dátum"
                value={startDate}
                onChange={(date) => setStartDate(date ? dayjs(date) : null)}
                sx={{ width: '100%' }}
              />
            </Box>

            <Box sx={{ flex: 1, minWidth: 200 }}>
              <DatePicker
                label="Befejező dátum"
                value={endDate}
                onChange={(date) => setEndDate(date ? dayjs(date) : null)}
                sx={{ width: '100%' }}
              />
            </Box>

            <Box sx={{ flex: 1, minWidth: 200 }}>
              <Button
                fullWidth
                variant="contained"
                onClick={() => loadReport(1)}
                disabled={loading}
                startIcon={<SecurityIcon />}
              >
                Szűrés
              </Button>
            </Box>
          </Box>
        </Paper>

        {/* Summary Cards */}
        {reportData && !loading && (
          <Box sx={{ display: 'flex', gap: 3, flexWrap: 'wrap', mb: 3 }}>
            <Card sx={{ flex: 1, minWidth: 200 }}>
              <CardContent sx={{ textAlign: 'center' }}>
                <SecurityIcon sx={{ fontSize: 40, color: 'error.main', mb: 1 }} />
                <Typography variant="h4" color="error.main">
                  {reportData.totalCount}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Összes hozzáférés
                </Typography>
              </CardContent>
            </Card>

            <Card sx={{ flex: 1, minWidth: 200 }}>
              <CardContent sx={{ textAlign: 'center' }}>
                <PersonIcon sx={{ fontSize: 40, color: 'primary.main', mb: 1 }} />
                <Typography variant="h4" color="primary.main">
                  {new Set(reportData.data.map(log => log.coachName)).size}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Érintett edzők
                </Typography>
              </CardContent>
            </Card>

            <Card sx={{ flex: 1, minWidth: 200 }}>
              <CardContent sx={{ textAlign: 'center' }}>
                <PersonIcon sx={{ fontSize: 40, color: 'success.main', mb: 1 }} />
                <Typography variant="h4" color="success.main">
                  {new Set(reportData.data.map(log => log.viewedUserName)).size}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Érintett játékosok
                </Typography>
              </CardContent>
            </Card>

            <Card sx={{ flex: 1, minWidth: 200 }}>
              <CardContent sx={{ textAlign: 'center' }}>
                <EventIcon sx={{ fontSize: 40, color: 'warning.main', mb: 1 }} />
                <Typography variant="h4" color="warning.main">
                  {new Set(reportData.data.map(log => log.eventName)).size}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Érintett események
                </Typography>
              </CardContent>
            </Card>
          </Box>
        )}

        {/* Report Data */}
        {reportData && reportData.data.length > 0 && (
          <Paper sx={{ mb: 3 }}>
            <Box sx={{ p: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <Typography variant="h6">
                Hozzáférési Napló ({reportData.totalCount} bejegyzés)
              </Typography>
            </Box>
            <Divider />

            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>ID</TableCell>
                    <TableCell>Időpont</TableCell>
                    <TableCell>Edző</TableCell>
                    <TableCell>Megtekintett Játékos</TableCell>
                    <TableCell>Esemény</TableCell>
                    <TableCell>IP Cím</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {loading ? (
                    <TableRow>
                      <TableCell colSpan={6} align="center">
                        <CircularProgress />
                      </TableCell>
                    </TableRow>
                  ) : (
                    reportData.data.map((log) => (
                      <TableRow key={log.sosAccessLogId} hover>
                        <TableCell>
                          <Chip
                            label={`#${log.sosAccessLogId}`}
                            size="small"
                            variant="outlined"
                          />
                        </TableCell>
                        <TableCell>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <AccessTimeIcon sx={{ fontSize: 18, color: 'text.secondary' }} />
                            <Typography variant="body2">
                              {formatDateTime(log.accessedAt)}
                            </Typography>
                          </Box>
                        </TableCell>
                        <TableCell>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <PersonIcon sx={{ fontSize: 18, color: 'primary.main' }} />
                            <Typography variant="body2" fontWeight="medium">
                              {log.coachName}
                            </Typography>
                          </Box>
                        </TableCell>
                        <TableCell>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <PersonIcon sx={{ fontSize: 18, color: 'success.main' }} />
                            <Typography variant="body2" fontWeight="medium">
                              {log.viewedUserName}
                            </Typography>
                          </Box>
                        </TableCell>
                        <TableCell>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <EventIcon sx={{ fontSize: 18, color: 'warning.main' }} />
                            <Typography variant="body2">
                              {log.eventName}
                            </Typography>
                          </Box>
                        </TableCell>
                        <TableCell>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <ComputerIcon sx={{ fontSize: 18, color: 'text.secondary' }} />
                            <Typography variant="body2" color="text.secondary">
                              {log.ipAddress}
                            </Typography>
                          </Box>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>

            {/* Pagination */}
            {reportData.totalPages > 1 && (
              <Box sx={{ p: 2, display: 'flex', justifyContent: 'center' }}>
                <Pagination
                  count={reportData.totalPages}
                  page={pageNumber}
                  onChange={handlePageChange}
                  color="primary"
                  showFirstButton
                  showLastButton
                />
              </Box>
            )}
          </Paper>
        )}

        {/* Empty state */}
        {!loading && reportData && reportData.data.length === 0 && (
          <Paper sx={{ p: 4, textAlign: 'center' }}>
            <SecurityIcon sx={{ fontSize: 60, color: 'text.secondary', mb: 2 }} />
            <Typography variant="h6" color="text.secondary" gutterBottom>
              Nincs találat
            </Typography>
            <Typography variant="body2" color="text.secondary">
              A megadott szűrési feltételekkel nem található SOS hozzáférési napló.
            </Typography>
          </Paper>
        )}

        {/* GDPR Info */}
        <Alert severity="info" sx={{ mt: 3 }}>
          <Typography variant="body2" fontWeight="medium" gutterBottom>
            GDPR megfelelőség
          </Typography>
          <Typography variant="body2">
            Ez a napló teljes mértékben megfelel a GDPR követelményeinek. Minden vészhelyzeti adathozzáférés naplózásra kerül,
            beleértve a ki, mit, mikor, miért és honnan kérdéseket. A felhasználók kérhetik a rájuk vonatkozó hozzáférési
            naplók megtekintését.
          </Typography>
        </Alert>
      </Box>
    </LocalizationProvider>
  );
};

export default SosAccessLogReport;
